import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Tooltip,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { Edit, Save, Cancel, Preview, ExpandMore, Info } from '@mui/icons-material';
import { useEmailsettings } from '../hooks/useEmailSettings';
import { useThemes } from '../../../components/Theme/theme';
import { showSuccessSnackbar, showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { TemplateUpdateConfirmationDialog, TemplateSaveOption } from './TemplateUpdateConfirmationDialog';
import { DEFAULT_TEMPLATE_ID, UI_CONSTANTS, EMAIL_PLACEHOLDERS, MESSAGES } from '../constants/emailConstants';

interface EmailTemplateData {
  templateid: number | string;
  emailbody: string;
  templatename?: string;
  [key: string]: any;
}

interface EmailTemplatePreviewProps {
  templateId?: number;
  onSaveTemplate?: (templateData: EmailTemplateData) => void;
  onTemplateSaveStatusChange?: (isSaved: boolean) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onGetCurrentTemplateContent?: (getContent: () => string) => void;
  onGetSaveFunction?: (saveFunction: () => Promise<void>) => void;
  onGetShowUnsavedChangesPopup?: (showFunction: () => boolean) => void;
  onConfirmTemplateUpdate?: (option: TemplateSaveOption) => void;
  onCancelTemplateUpdate?: () => void;
  resetOnClose?: boolean;
  customContentForThisEmail?: string | null;
  onSetCustomContentForThisEmail?: (content: string | null) => void;
  onPreviewReady?: (ready: boolean) => void;
}

export const EmailTemplatePreview: React.FC<EmailTemplatePreviewProps> = ({
  templateId = DEFAULT_TEMPLATE_ID,
  onSaveTemplate,
  onTemplateSaveStatusChange,
  onUnsavedChangesChange,
  onGetCurrentTemplateContent,
  onGetSaveFunction,
  onGetShowUnsavedChangesPopup,
  onConfirmTemplateUpdate,
  onCancelTemplateUpdate,
  resetOnClose = false,
  customContentForThisEmail: parentCustomContent,
  onSetCustomContentForThisEmail,
  onPreviewReady,
}) => {
  const { theme } = useThemes();
  const accordionMaxHeight = UI_CONSTANTS.ACCORDION_MAX_HEIGHT;
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<EmailTemplateData | null>(null);
  const [localCustomContent, setLocalCustomContent] = useState<string | null>(null);
  
  const hasInitializedEdit = useRef(false);
  const customContentForThisEmail = parentCustomContent !== undefined ? parentCustomContent : localCustomContent;

  const onSaveComplete = useCallback(() => {
    setIsEditing(false);
    showSuccessSnackbar(MESSAGES.SUCCESS.UPDATE);
  }, []);

  const {
    data,
    TemplateList,
    saveChanges,
    isEditEnabled,
    setIsEditEnabled,
    isTemplateSaved,
    loadingEMaster,
    loadingTList,
  } = useEmailsettings({
    select: templateId,
    onSaveComplete,
    parent: 'modal',
  });

  const isTemplateSavedMemo = useCallback((id: number) => isTemplateSaved(id), [isTemplateSaved]);

  const getTemplateName = useCallback(
    (templateId: number) => {
      const template = TemplateList.find((t: any) => t.templateid === templateId);
      return template?.templatename || `Template ${templateId}`;
    },
    [TemplateList]
  );

  const hasUnsavedChanges = useMemo(
    () => isEditing && editedContent !== (data?.emailbody || ''),
    [isEditing, editedContent, data?.emailbody]
  );

  const isLoading = useMemo(() => loadingEMaster || loadingTList, [loadingEMaster, loadingTList]);

  const getCurrentContent = useCallback(
    () => {
      // Priority: customContentForThisEmail > editedContent (if editing) > original template
      if (customContentForThisEmail) {
        return customContentForThisEmail;
      }
      if (isEditing && editedContent) {
        return editedContent;
      }
      return data?.emailbody || '';
    },
    [customContentForThisEmail, editedContent, data?.emailbody, isEditing]
  );

  const saveFunction = useCallback(async () => {
    const hasChanges = editedContent !== (data?.emailbody || '');
    const isFirstSave = !isTemplateSavedMemo(templateId);

    if (isEditing && (isFirstSave || hasChanges)) {
      const updatedData: EmailTemplateData = { ...data, emailbody: editedContent };
      await saveChanges(updatedData);
      onSaveTemplate?.(updatedData);
      setIsEditing(false);
      showSuccessSnackbar(MESSAGES.SUCCESS.SAVE);
      onTemplateSaveStatusChange?.(true);
      onUnsavedChangesChange?.(false);
    }
  }, [isEditing, editedContent, data, isTemplateSavedMemo, templateId, saveChanges, onSaveTemplate, onTemplateSaveStatusChange, onUnsavedChangesChange]);

  const showUnsavedChangesPopup = useCallback(() => {
    if (hasUnsavedChanges) {
      setPendingSaveData({ ...data, emailbody: editedContent });
      setShowUpdateConfirmation(true);
      return true;
    }
    return false;
  }, [hasUnsavedChanges, data, editedContent]);

  // Update parent callbacks and notify when template preview is ready (prevents sending without email body)
  useEffect(() => {
    if (onTemplateSaveStatusChange && data) {
      onTemplateSaveStatusChange(isTemplateSavedMemo(templateId));
    }
    onUnsavedChangesChange?.(hasUnsavedChanges);
    onGetCurrentTemplateContent?.(getCurrentContent);
    onGetSaveFunction?.(saveFunction);
    onGetShowUnsavedChangesPopup?.(showUnsavedChangesPopup);
    const ready = !isLoading && !!data;
    onPreviewReady?.(ready);
  }, [
    data,
    templateId,
    isTemplateSavedMemo,
    hasUnsavedChanges,
    isLoading,
    getCurrentContent,
    saveFunction,
    showUnsavedChangesPopup,
    onTemplateSaveStatusChange,
    onUnsavedChangesChange,
    onGetCurrentTemplateContent,
    onGetSaveFunction,
    onGetShowUnsavedChangesPopup,
    onPreviewReady,
  ]);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    // Use custom content if available, otherwise use original template
    const contentToEdit = customContentForThisEmail || data?.emailbody || '';
    setEditedContent(contentToEdit);
    setIsEditEnabled(true);
  }, [data?.emailbody, customContentForThisEmail, setIsEditEnabled]);

  const handleSaveClick = useCallback(() => {
    const hasChanges = editedContent !== (data?.emailbody || '');
    if (!hasChanges) {
      showWarningSnackbar(MESSAGES.WARNING.NO_CHANGES);
      return;
    }
    setPendingSaveData({ ...data, emailbody: editedContent });
    setShowUpdateConfirmation(true);
  }, [editedContent, data]);

  const handleCancelClick = useCallback(() => {
    setIsEditing(false);
    setIsEditEnabled(false);
  }, [setIsEditEnabled]);

  const handleConfirmUpdate = useCallback(
    async (option: TemplateSaveOption) => {
      if (!pendingSaveData) return;

      try {
        if (option === 'save_permanently') {
          await saveChanges(pendingSaveData);
          onSaveTemplate?.(pendingSaveData);
          setIsEditing(false);
          showSuccessSnackbar(MESSAGES.SUCCESS.UPDATE);
          onTemplateSaveStatusChange?.(true);
          onUnsavedChangesChange?.(false);
        } else if (option === 'this_email_only') {
          const customContent = pendingSaveData.emailbody || editedContent;
          if (onSetCustomContentForThisEmail) {
            onSetCustomContentForThisEmail(customContent);
          } else {
            setLocalCustomContent(customContent);
          }
          setEditedContent(customContent);
          setIsEditing(false);
          hasInitializedEdit.current = true;
          showSuccessSnackbar('Template content will be used for this email only');
          onUnsavedChangesChange?.(false);
        } else if (option === 'discard') {
          setIsEditing(false);
          setEditedContent(data?.emailbody || '');
          if (onSetCustomContentForThisEmail) {
            onSetCustomContentForThisEmail(null);
          } else {
            setLocalCustomContent(null);
          }
          showSuccessSnackbar(MESSAGES.SUCCESS.CANCEL);
          onUnsavedChangesChange?.(false);
          onCancelTemplateUpdate?.();
        }
        onConfirmTemplateUpdate?.(option);
      } catch (error) {
        showErrorSnackbar(MESSAGES.ERROR.SAVE_FAILED);
      } finally {
        setShowUpdateConfirmation(false);
        setPendingSaveData(null);
      }
    },
    [pendingSaveData, editedContent, data, saveChanges, onSaveTemplate, onTemplateSaveStatusChange, onUnsavedChangesChange, onConfirmTemplateUpdate, onCancelTemplateUpdate, onSetCustomContentForThisEmail]
  );

  const handleCancelUpdate = useCallback(() => {
    setShowUpdateConfirmation(false);
    setPendingSaveData(null);
  }, []);

  const handlePlaceholderClick = useCallback((placeholderName: string) => {
    navigator.clipboard.writeText(placeholderName);
  }, []);

  // Initialize template content - only on first load, not when switching tabs
  useEffect(() => {
    if (data && !isEditing && !hasInitializedEdit.current) {
      const templateContent = customContentForThisEmail || data?.emailbody || '';
      if (templateContent) {
        hasInitializedEdit.current = true;
        setEditedContent(templateContent);
      }
    }
  }, [data, isEditing, customContentForThisEmail]);

  // Reset on close
  useEffect(() => {
    if (resetOnClose) {
      if (onSetCustomContentForThisEmail) {
        onSetCustomContentForThisEmail(null);
      } else {
        setLocalCustomContent(null);
      }
      setEditedContent(data?.emailbody || '');
      setIsEditing(false);
      hasInitializedEdit.current = false;
    }
  }, [resetOnClose, data?.emailbody, onSetCustomContentForThisEmail]);

  const isDarkTheme = useMemo(() => (theme?.palette as any)?.TmId?.id?.toString().includes('9'), [theme]);

  const paperStyles = useMemo(
    () => ({
      p: { xs: 1, sm: 1.5 },
      borderRadius: 2,
      backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
      border: `1px solid ${theme?.palette?.divider}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }),
    [isDarkTheme, theme?.palette?.divider]
  );

  const textFieldStyles = useMemo(
    () => ({
      flex: 1,
      '& .MuiOutlinedInput-root': {
        backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.08)' : 'white',
        borderRadius: 1,
        height: '100%',
        border: isEditing ? undefined : `2px solid ${theme?.palette?.success.main}20`,
        '& fieldset': {
          borderColor: isEditing ? theme?.palette?.divider : theme?.palette?.success.main,
          borderWidth: '1px',
        },
        '&:hover fieldset': { borderColor: theme?.palette?.primary.main },
        '&.Mui-focused fieldset': { borderColor: theme?.palette?.primary.main, borderWidth: '2px' },
      },
      '& .MuiInputBase-input': {
        fontSize: { xs: '13px', sm: '14px' },
        lineHeight: 1.6,
        color: isDarkTheme ? 'white' : 'text.primary',
        height: '100% !important',
        overflow: 'auto !important',
        fontWeight: isEditing ? 'normal' : 500,
        opacity: 1,
      },
    }),
    [isDarkTheme, theme, isEditing]
  );

  const LoadingSkeleton = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: 'primary.main', mr: 1 }} />
            <Skeleton variant="text" width={200} height={32} />
          </Box>
          <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
        </Box>
        <Divider />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
        <Box sx={{ mt: 1 }}>
          <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    ),
    []
  );

  if (isLoading) {
    return (
      <Paper elevation={2} sx={paperStyles}>
        {LoadingSkeleton}
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={paperStyles}>
      {/* Header */}
      <Box sx={{ mb: 1, pb: 1, borderBottom: '2px solid', borderColor: 'divider', background: 'linear-gradient(to right, rgba(145, 27, 124, 0.03), transparent)', borderRadius: 1, px: 1, pt: 0.75 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(145, 27, 124, 0.3)' }}>
              <Preview sx={{ fontSize: 18, color: 'white' }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDarkTheme ? 'white' : 'text.primary',
                  fontSize: '14px',
                  mb: 0.25,
                  ...(!isDarkTheme && {
                    background: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }),
                }}
              >
                {getTemplateName(templateId)} - Email Template Preview
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditing ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelClick}
                  startIcon={<Cancel />}
                  size="small"
                  disabled={!isTemplateSavedMemo(templateId)}
                  sx={{
                    color: (theme?.palette as any)?.TmId?.id?.toString().includes('9') ? 'white' : 'text.primary',
                    borderColor: theme?.palette?.divider,
                    backgroundColor: (theme?.palette as any)?.TmId?.id?.toString().includes('9') ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: { xs: '12px', sm: '13px' },
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    minWidth: 'auto',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: (theme?.palette as any)?.TmId?.id?.toString().includes('9') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      borderColor: theme?.palette?.text.secondary,
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveClick}
                  startIcon={<Save />}
                  size="small"
                  sx={{
                    backgroundColor: '#ff6600',
                    color: 'white',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: { xs: '12px', sm: '13px' },
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    minWidth: 'auto',
                    boxShadow: '0 2px 4px rgba(255, 102, 0, 0.3)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { backgroundColor: '#e55a00', boxShadow: '0 4px 8px rgba(255, 102, 0, 0.4)', transform: 'translateY(-1px)' },
                    '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 4px rgba(255, 102, 0, 0.3)' },
                  }}
                >
                  Save
                </Button>
              </>
            ) : (
              <Tooltip title="Edit Template" arrow>
                <Button
                  variant="outlined"
                  onClick={handleEditClick}
                  startIcon={<Edit />}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    backgroundColor: (theme?.palette as any)?.TmId?.id?.toString().includes('9') ? 'rgba(255, 255, 255, 0.05)' : 'rgba(25, 118, 210, 0.04)',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: { xs: '12px', sm: '13px' },
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    minWidth: 'auto',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { backgroundColor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main', boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)', transform: 'translateY(-1px)' },
                    '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' },
                  }}
                >
                  Edit
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      <Box sx={{ mb: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: 'calc(100% - 60px)', overflow: 'hidden', position: 'relative' }}>
        {isEditing ? (
          <TextField
            multiline
            fullWidth
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            minRows={UI_CONSTANTS.TEXTAREA_MIN_ROWS}
            maxRows={UI_CONSTANTS.TEXTAREA_MAX_ROWS}
            placeholder="Enter your email template content here..."
            sx={textFieldStyles}
            InputProps={{
              classes: { input: 'custom-scrollbar' },
            }}
          />
        ) : (
          <TextField
            multiline
            fullWidth
            value={getCurrentContent()}
            sx={textFieldStyles}
            InputProps={{
              readOnly: true,
              classes: { input: 'custom-scrollbar' },
            }}
          />
        )}
      </Box>

      <Accordion
        sx={{
          mt: 0.25,
          mb: 0,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '4px 0 0 0' },
          '& .MuiAccordionSummary-root': { '&.Mui-expanded': { minHeight: 'auto' } },
          '& .MuiAccordionDetails-root': { paddingTop: 0 },
        }}
        defaultExpanded={false}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.08)' : 'grey.100',
            borderRadius: '8px',
            border: `1px solid ${theme?.palette?.divider}`,
            '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, margin: '8px 0' },
            '&.Mui-expanded': { minHeight: 'auto', '& .MuiAccordionSummary-content': { margin: '8px 0' } },
            minHeight: { xs: 40, sm: 44 },
            '&:hover': { backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.12)' : 'grey.200' },
          }}
        >
          <Info sx={{ fontSize: { xs: 14, sm: 16 }, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '13px', sm: '14px' }, color: isDarkTheme ? 'white' : 'text.primary' }}>
            Available Placeholders
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.02)' : 'grey.50', borderRadius: '0 0 8px 8px', border: `1px solid ${theme?.palette?.divider}`, borderTop: 'none', maxHeight: accordionMaxHeight, overflow: 'auto' }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: isDarkTheme ? 'white' : 'text.secondary', fontSize: { xs: '11px', sm: '12px' }, fontWeight: 500 }}>
              {MESSAGES.INFO.PLACEHOLDER_INSTRUCTION}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 0.5, sm: 0.75 },
                justifyContent: { xs: 'center', sm: 'flex-start' },
                maxHeight: UI_CONSTANTS.PLACEHOLDER_MAX_HEIGHT,
                overflow: 'auto',
                p: 1,
                backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : 'white',
                borderRadius: '6px',
                border: `1px solid ${theme?.palette?.divider}`,
              }}
            >
              {EMAIL_PLACEHOLDERS.map((placeholder, index) => (
                <Tooltip key={index} title={placeholder.description} arrow>
                  <Chip
                    label={placeholder.name}
                    size="small"
                    onClick={() => handlePlaceholderClick(placeholder.name)}
                    sx={{
                      backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.15)' : 'primary.light',
                      color: isDarkTheme ? 'white' : 'primary.contrastText',
                      fontSize: { xs: '10px', sm: '11px' },
                      height: { xs: 24, sm: 26 },
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                      '&:hover': { backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.25)' : 'primary.main', transform: 'scale(1.05)' },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
          <Alert
            severity="info"
            sx={{
              backgroundColor: isDarkTheme ? 'rgba(33, 150, 243, 0.15)' : 'info.light',
              color: isDarkTheme ? 'white' : 'info.contrastText',
              fontSize: { xs: '11px', sm: '12px' },
              py: { xs: 0.75, sm: 1 },
              borderRadius: '6px',
              border: `1px solid ${theme?.palette?.info.main}`,
              '& .MuiAlert-icon': { fontSize: { xs: '14px', sm: '16px' } },
            }}
          >
            <Typography variant="caption" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
              {MESSAGES.INFO.PLACEHOLDER_DESCRIPTION}
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      <TemplateUpdateConfirmationDialog open={showUpdateConfirmation} onClose={handleCancelUpdate} onConfirm={handleConfirmUpdate} onCancel={handleCancelUpdate} templateName={getTemplateName(templateId)} />
    </Paper>
  );
};

export default EmailTemplatePreview;
